import React, { useMemo, useState } from 'react';
import { NoteDestination } from '../utils/types';
import useNotes, {
    NoteQueryArgs,
    ResourceNoteQueryArgs,
    getDefaultNoteFromNoteDestination,
} from '../../../../hooks/api/useNotes';
import { getIntl } from '../../../../utils/intl';
import { APITypes } from '../../../../primary-record-types';
import {
    noteEntityTypeToPermissionDomain,
    useNoteQueryArgsResourceCategory,
} from '../../../../hooks/api/useHasNotePermission';
import NoteCard from '../../NoteCard';
import NoteSearch, { filterNotesBySearchText } from './NoteSearch';
import { filterNotesBySource, getNoteSourceOptions } from './noteSourceFilter';
import { useCollaborators } from '../../../../hooks/api/useCollaborators';
import { AnimateLoadedContent } from '../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../v2BaseComponents/ActionButtonLayout';
import HeaderContainer from '../../../v2BaseComponents/HeaderContainer';
import Stack, { ContentStack } from '../../../v2BaseComponents/Stack';
import * as Filtering from '../../Filtering';
import FilterButton from '../../Filtering/FilterButton';
import MainContentContainer from '../../../v2BaseComponents/MainContentContainer';
import EmptyStateHint, { EmptyStateVariant, LinkEmptyStateHint } from '../../EmptyStateHint';
import { useGetHasPermission } from '../../../v2BaseComponents/Permissions/hooks';
import useNotify from '../../../../hooks/useNotify';
import { View } from 'react-native';
import useOrganization from '../../../../hooks/api/useOrganization';
import { NotesList as NotesListType } from '../../../../primary-record-types/apiTypes/notesService';
import { useAppType } from '../../../../hooks/useAppType';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';
import { useIsInSideDrawerContainer } from '../../../v2BaseComponents/SideDrawer/SideDrawerContainer';
import { useMaybeRenderedSideDrawerToggle } from '../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import CopyOrganizationNotesSideDrawerContent from './CopyOrganizationNotesSideDrawerContent';
import { isPresent } from 'ts-is-present';
import { useLoadedUser } from '../../../../hooks/api/useUser';

const intl = getIntl('note-list', {
    addNote: 'Add Note',
    noNotesPlaceholder:
        'Share information not listed in healthcare systems. Describe routines, symptoms, care plans, questions, decisions, and have discussions around those topics with the people you’ve shared access with.',
    invalidDestinationError: 'Error creating note',
    addOrgResource: 'Add {organizationName} Resource',
    sortNotes: 'Sort Notes',
    newest: 'Newest',
    oldest: 'Oldest',
    date: 'Date',
    filterNotes: 'Filter Notes',
    source: 'Source',
});

export type NotesListOwnProps = {
    noteQueryArgs: NoteQueryArgs;
    /**
     * Additional pre-fetched notes to merge into the list (e.g. notes from
     * prior client records for a reinvited client). These are read-only in the
     * sense that the list's add-note destination is still driven by
     * `noteQueryArgs` — notes tapped here navigate via `onViewDetails` as usual.
     */
    additionalNotes?: APITypes.notesService.NoteListItem[];
    onAddNote: (destination: NoteDestination) => void;
    onViewDetails: (note: APITypes.notesService.NoteListItem) => void;
    linkEmptyStateHint?: boolean;
    emptyStateVariant?: EmptyStateVariant;
    flat?: boolean;
};

const isResourceNoteQueryArgs = (noteQueryArgs: NoteQueryArgs): noteQueryArgs is ResourceNoteQueryArgs => {
    return (
        noteQueryArgs.entityType === 'condition-resource' ||
        noteQueryArgs.entityType === 'medication-resource' ||
        noteQueryArgs.entityType === 'allergy-resource' ||
        noteQueryArgs.entityType === 'visit-resource' ||
        noteQueryArgs.entityType === 'vaccination-resource' ||
        noteQueryArgs.entityType === 'procedure-resource' ||
        noteQueryArgs.entityType === 'imaging-resource' ||
        noteQueryArgs.entityType === 'lab-resource'
    );
};

type NotApplicableProNotesResourcesData = {
    status: 'not-applicable';
};

type LoadingProNotesResourcesData = {
    status: 'loading';
};

type LoadedProNotesResourcesData = {
    status: 'loaded';
    organizationNotes: NotesListType;
    organizationName: string;
    profileId: string;
};

type ProNotesResourcesData =
    | NotApplicableProNotesResourcesData
    | LoadingProNotesResourcesData
    | LoadedProNotesResourcesData;

type NoteListContentProps = {
    proNotesResourcesData: ProNotesResourcesData;
} & NotesListOwnProps;

type ProNotesResourcesProviderProps = {
    children: (props: NoteListContentProps) => React.ReactNode;
} & NotesListProps;

type ProfessionalAppNotesResourcesProviderProps = {
    profileId: string;
} & ProNotesResourcesProviderProps;

const ProfessionalAppNotesResourcesProvider: React.FC<ProfessionalAppNotesResourcesProviderProps> = ({
    children,
    profileId,
    ...toPass
}) => {
    const { myOrganization, isLoadingMyOrganization } = useOrganization({});
    const { notes, isLoading } = useNotes(
        myOrganization
            ? {
                  noteQueryArgs: {
                      entityType: 'organization',
                      organizationId: myOrganization.organization.organizationId,
                  },
              }
            : {},
    );
    if (isLoading || isLoadingMyOrganization) {
        return children({
            ...toPass,
            proNotesResourcesData: { status: 'loading' },
        });
    } else {
        return children({
            ...toPass,
            proNotesResourcesData: {
                status: 'loaded',
                organizationNotes: notes,
                organizationName: myOrganization?.organization.metadata.name || '',
                profileId,
            },
        });
    }
};

const ProNotesResourcesProvider: React.FC<ProNotesResourcesProviderProps> = ({ children, ...toPass }) => {
    const appType = useAppType();
    const isInSideDrawer = useIsInSideDrawerContainer();
    const isMobileLayout = useIsMobileLayout(); // only show on desktop due to side drawer.  If this is needed in the future, we can add a mobile layout
    const getNoteDestinationProfileId: () => string | null = () => {
        if (
            toPass.noteQueryArgs.entityType === 'user' ||
            toPass.noteQueryArgs.entityType === 'organization' ||
            toPass.noteQueryArgs.entityType === 'organization-client-record'
        ) {
            return null;
        } else {
            return toPass.noteQueryArgs.medicalProfileId;
        }
    };
    const profileId = getNoteDestinationProfileId();
    if (appType !== 'professional' || isMobileLayout || !profileId || !isInSideDrawer) {
        return children({
            ...toPass,
            proNotesResourcesData: { status: 'not-applicable' },
        });
    } else {
        return (
            <ProfessionalAppNotesResourcesProvider profileId={profileId} {...toPass}>
                {children}
            </ProfessionalAppNotesResourcesProvider>
        );
    }
};

export type NotesListProps = NotesListOwnProps;

const getNoteSortValue = (note: APITypes.notesService.NoteListItem) =>
    // Prefer the date the note's content is about (e.g. the visit date) so imported clinical notes sort by
    // when the care happened, not when they landed in Primary Record. Falls back to update/import time.
    note.contentDate ?? new Date(note.updatedAt || note.createdAt).getTime();

const NotesListContent: React.FC<NoteListContentProps> = (props) => {
    const { user } = useLoadedUser({});
    const notify = useNotify();
    const { isLoading, notes } = useNotes({
        noteQueryArgs: props.noteQueryArgs,
    });

    const [searchText, setSearchText] = useState('');
    const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
    const [sourceFilter, setSourceFilter] = useState<string[] | null>(null);
    const combinedNotes = useMemo(() => {
        if (!props.additionalNotes?.length) return notes.notes;
        const byId = new Map<string, APITypes.notesService.NoteListItem>();
        for (const note of notes.notes) byId.set(note.noteId, note);
        for (const note of props.additionalNotes) if (!byId.has(note.noteId)) byId.set(note.noteId, note);
        return Array.from(byId.values());
    }, [notes.notes, props.additionalNotes]);

    // Resolve organization names for the Source filter chips: profile lists get them
    // from the profile's organization collaborators, the professional app from its own
    // organization (both hooks no-op where they don't apply).
    const notesMedicalProfileId =
        'medicalProfileId' in props.noteQueryArgs ? props.noteQueryArgs.medicalProfileId || null : null;
    const { organizationCollaborators } = useCollaborators({
        medicalProfileId: notesMedicalProfileId,
        disableInviteLoad: true,
    });
    const { myOrganization } = useOrganization({});
    const sourceOptions = useMemo(
        () =>
            getNoteSourceOptions(combinedNotes, (organizationId) => {
                if (myOrganization?.organization.organizationId === organizationId) {
                    return myOrganization.organization.metadata.name;
                }
                const collaborator = organizationCollaborators.find(
                    (organizationCollaborator) =>
                        organizationCollaborator.organization.organizationId === organizationId,
                );
                return collaborator?.organization.metadata.name || null;
            }),
        [combinedNotes, organizationCollaborators, myOrganization],
    );

    const sortedNotes = useMemo(
        () =>
            [...combinedNotes].sort((a, b) =>
                sortOrder === 'newest'
                    ? getNoteSortValue(b) - getNoteSortValue(a)
                    : getNoteSortValue(a) - getNoteSortValue(b),
            ),
        [combinedNotes, sortOrder],
    );
    const filteredNotes = useMemo(
        () => filterNotesBySearchText(filterNotesBySource(sortedNotes, sourceFilter), searchText),
        [sortedNotes, sourceFilter, searchText],
    );

    const noteResourceCategory = useNoteQueryArgsResourceCategory(props.noteQueryArgs);
    const getHasPermission = useGetHasPermission(
        props.noteQueryArgs.entityType === 'user' ||
            props.noteQueryArgs.entityType === 'organization' ||
            props.noteQueryArgs.entityType === 'organization-client-record'
            ? null
            : props.noteQueryArgs.medicalProfileId,
    );
    const getCanAddNote = () => {
        const entityType = props.noteQueryArgs.entityType;
        if (entityType === 'user' || entityType === 'organization' || entityType === 'organization-client-record')
            return true;

        if (entityType === 'medical-profile') return true;

        if (entityType === 'care-team-member' || entityType === 'care-team') {
            return getHasPermission('careTeam', 'edit');
        }

        if (noteResourceCategory.isLoading || !noteResourceCategory.resourceCategory) return false;

        const permissionDomain = noteEntityTypeToPermissionDomain[noteResourceCategory.resourceCategory];
        // @ts-expect-error TS2345
        return getHasPermission(permissionDomain, 'edit');
    };

    const getNoteDestination = (): NoteDestination | null => {
        if (isResourceNoteQueryArgs(props.noteQueryArgs)) {
            return {
                entityType: props.noteQueryArgs.entityType,
                medicalProfileId: props.noteQueryArgs.medicalProfileId,
                entityId: props.noteQueryArgs.resourceId,
            };
        } else if (props.noteQueryArgs.entityType === 'medical-profile') {
            if (props.noteQueryArgs.medicalProfileId) {
                return {
                    entityType: 'medical-profile',
                    medicalProfileId: props.noteQueryArgs.medicalProfileId,
                };
            } else {
                return null;
            }
        } else {
            return props.noteQueryArgs;
        }
    };

    const addProNotesResourcesSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: `addProNote-${JSON.stringify(props.noteQueryArgs)}`,
        title:
            props.proNotesResourcesData.status === 'loaded'
                ? intl('addOrgResource', {
                      organizationName: props.proNotesResourcesData.organizationName,
                  })
                : '',
        renderContent: (onClose) => {
            const noteDestination = getNoteDestination();
            if (!noteDestination) return null;

            const { entityId, entityType } = getDefaultNoteFromNoteDestination(noteDestination, user.userId);
            if (props.proNotesResourcesData.status === 'loaded' && entityId && entityType) {
                return (
                    <CopyOrganizationNotesSideDrawerContent
                        destinationMedicalProfileId={props.proNotesResourcesData.profileId}
                        onClose={onClose}
                        noteDestination={{ entityId, entityType }}
                    />
                );
            } else {
                return null;
            }
        },
        mobileOnFocus: () => {}, // only for desktop
    });

    const noteDestination = getNoteDestination();

    return (
        <>
            <View style={{ flex: 1 }}>
                <HeaderContainer>
                    <Filtering.Container
                        renderSearch={() => (
                            <NoteSearch
                                searchText={searchText}
                                onSearchTextChange={setSearchText}
                                isDisabled={!combinedNotes.length}
                                style={{ width: '100%' }}
                            />
                        )}
                        renderFilterButton={() => (
                            <Stack horizontal space={2}>
                                {sourceOptions.length > 1 ? (
                                    <FilterButton
                                        buttonTestID="notes-filter-button"
                                        filterPromptTitle={intl('filterNotes')}
                                        activeFilterCount={sourceFilter?.length || 0}
                                        onResetFilters={() => setSourceFilter(null)}
                                        renderFilterContents={() => (
                                            <Filtering.FilterPopoverContent resetDisabled={!sourceFilter?.length}>
                                                <Filtering.MultiSelectFilter
                                                    title={intl('source')}
                                                    value={sourceFilter}
                                                    options={sourceOptions.map((option) => ({
                                                        label: option.label,
                                                        value: option.key,
                                                        count: option.count,
                                                    }))}
                                                    onChange={setSourceFilter}
                                                    onClear={() => setSourceFilter(null)}
                                                />
                                            </Filtering.FilterPopoverContent>
                                        )}
                                    />
                                ) : null}
                                <FilterButton
                                    buttonIcon="VerticalArrows"
                                    buttonTestID="notes-sort-button"
                                    filterPromptTitle={intl('sortNotes')}
                                    popoverTitle={intl('sortNotes')}
                                    onResetFilters={() => setSortOrder('newest')}
                                    renderFilterContents={() => (
                                        <Filtering.FilterPopoverContent resetDisabled={sortOrder === 'newest'}>
                                            <Filtering.SelectFilter
                                                onClear={() => setSortOrder('newest')}
                                                title={intl('date')}
                                                value={sortOrder}
                                                options={[
                                                    { label: intl('newest'), value: 'newest' },
                                                    { label: intl('oldest'), value: 'oldest' },
                                                ]}
                                                onChange={(sort) => {
                                                    if (sort) setSortOrder(sort);
                                                }}
                                            />
                                        </Filtering.FilterPopoverContent>
                                    )}
                                />
                            </Stack>
                        )}
                    />
                </HeaderContainer>
                <ActionButtonLayout
                    scrollable
                    whiteBackground={props.flat}
                    actions={[
                        {
                            title: intl('addNote'),
                            icon: 'PlusCircle' as const,
                            onPress: () => {
                                const noteDestination = getNoteDestination();
                                if (!noteDestination) {
                                    notify('error', intl('invalidDestinationError'));
                                } else {
                                    props.onAddNote(noteDestination);
                                }
                            },
                            testID: 'add-note-button',
                            show: getCanAddNote(),
                            displayInMoreMenu: false,
                        },
                        props.proNotesResourcesData.status === 'loaded' &&
                        !!props.proNotesResourcesData.organizationNotes.notes.length
                            ? {
                                  title: intl('addOrgResource', {
                                      organizationName: props.proNotesResourcesData.organizationName,
                                  }),
                                  onPress: addProNotesResourcesSideDrawer.onOpen,
                                  icon: 'PlusCircle' as const,
                                  show: getCanAddNote() && !!noteDestination,
                                  testID: 'add-org-note-button',
                                  displayInMoreMenu: false,
                              }
                            : null,
                    ].filter(isPresent)}
                >
                    <>
                        <AnimateLoadedContent isLoading={isLoading}>
                            {() => (
                                <MainContentContainer testID="notes-list-content-container">
                                    {!combinedNotes.length ? (
                                        props.linkEmptyStateHint ? (
                                            <LinkEmptyStateHint variant="notes" />
                                        ) : (
                                            <EmptyStateHint variant={props.emptyStateVariant || 'notes'} />
                                        )
                                    ) : (
                                        <ContentStack>
                                            {filteredNotes.map((note) => (
                                                <NoteCard
                                                    key={note.noteId}
                                                    note={note}
                                                    onPress={() => {
                                                        props.onViewDetails(note);
                                                    }}
                                                    flat={props.flat}
                                                />
                                            ))}
                                        </ContentStack>
                                    )}
                                </MainContentContainer>
                            )}
                        </AnimateLoadedContent>
                    </>
                </ActionButtonLayout>
            </View>
            {addProNotesResourcesSideDrawer.SideDrawer}
        </>
    );
};

export const NotesList: React.FC<NotesListProps> = (props) => {
    return (
        <ProNotesResourcesProvider {...props}>{(props) => <NotesListContent {...props} />}</ProNotesResourcesProvider>
    );
};

export default React.memo(NotesList);
